/**
 * "Can the user actually see the page right now?" — the gate for anything that
 * runs on mount purely to be *watched*, which here is the two scroll hints
 * that show a sideways scroller has more in it than fits.
 *
 * Two ways such a hint plays to nobody:
 *
 * 1. The full-screen splash (`#initial-splash` in `app/layout.tsx`, dismissed
 *    by putting `hd-splash-done` on `<html>`) is still covering the page. On a
 *    cold load it outlives the point where the header has measured itself.
 * 2. The tab is in the background. Worse than unseen — `requestAnimationFrame`
 *    does not run in a hidden tab at all, so a hint started there freezes
 *    part-way and leaves the scroller parked mid-travel until the tab is
 *    looked at again.
 */

const splashGone = () =>
    document.documentElement.classList.contains('hd-splash-done') ||
    !document.getElementById('initial-splash');

const canBeSeen = () =>
    splashGone() && document.visibilityState === 'visible';

/**
 * Runs `run` at the first moment the page is on screen and uncovered —
 * synchronously if that is already true.
 *
 * @returns a cancel function; call it to stop waiting if the caller unmounts
 * first. `run` fires at most once.
 */
export const onPageVisibleToUser = (run: () => void): (() => void) => {
    if (canBeSeen()) {
        run();
        return () => {};
    }

    let settled = false;
    const teardowns: Array<() => void> = [];
    const stop = () => {
        settled = true;
        teardowns.forEach((teardown) => teardown());
    };

    const check = () => {
        if (settled || !canBeSeen()) return;
        stop();
        run();
    };

    const observer = new MutationObserver(check);
    observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class']
    });
    teardowns.push(() => observer.disconnect());

    document.addEventListener('visibilitychange', check);
    teardowns.push(() =>
        document.removeEventListener('visibilitychange', check)
    );

    return stop;
};
